import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    Output
} from '@angular/core';
import { EntityCollectionService, EntityServices } from '@ngrx/data';
import { ofType } from '@ngrx/effects';
import { ActionsSubject, select, Store } from '@ngrx/store';
import { ConfirmationService } from 'primeng/api';
import clone from 'just-clone';
import moment from 'moment';
import { combineLatest, Observable, ReplaySubject } from 'rxjs';
import {
    filter,
    map,
    take,
    takeWhile,
    tap,
    withLatestFrom
} from 'rxjs/operators';
import * as env from '../../../environments/environment';
import * as fromDataServices from '../../data-services';
import * as fromLookups from '../../lookups';
import { IServiceItem, ITask } from '../../lookups';
import * as fromShared from '../../shared';
import { LoggingService } from '../../shared/services/logging/logging.service';
import { IUserInfo } from '../../user-info';
import * as fromUser from '../../user-info/store';
import { ITimeCard } from '../itime-card';
import * as fromTimecard from '../store';
import { TimeCardDocumentation } from '../store/time-card-documentation.model';
import * as docReducers from '../store/time-card-documentation.reducer';
@Component({
    selector: 'ef-time-card-documentation-feature',
    templateUrl: './time-card-documentation-feature.component.html',
    styleUrls: ['./time-card-documentation-feature.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class TimeCardDocumentationFeatureComponent
    implements OnDestroy, AfterViewInit
{
    public environment: any;
    process$: Observable<fromShared.Process>;
    @Input() AddDocumentation$: Observable<ITimeCard>;
    @Input() showDocumentation: boolean;

    private TimeCardsSubject = new ReplaySubject<Array<ITimeCard>>(1);
    private _timecards: Array<ITimeCard>;
    @Input() public set TimeCards(value: Array<ITimeCard>) {
        this.log.verbose({
            type: 'TimeCardDocumentationFeatureComponent:TimeCards:set',
            value
        });
        this._timecards = clone(value);
        this.TimeCardsSubject.next(this._timecards);
    }
    public get TimeCards(): Array<ITimeCard> {
        return this._timecards;
    }
    @Input() public set action(value: string) {
        this.log.verbose({
            type: 'TimeCardDocumentationFeatureComponent:action:set',
            value
        });
        if (value) {
            this.validating = value.toLocaleLowerCase().includes('validate');
            this.approving = value.toLocaleLowerCase().includes('approve');
        }
    }
    public authClasses$: Observable<fromLookups.IAuthorizationClass[]>;
    public DocumentationSubject = new ReplaySubject<
        Array<TimeCardDocumentation>
    >(1);
    private deleteSubject = new ReplaySubject<TimeCardDocumentation>(1);
    public documentation$: Observable<Array<TimeCardDocumentation>>;
    selectedError$: Observable<fromShared.Errors.IShowErrors>;

    public includeClassify$: Observable<boolean>;
    public EmployeeIdSubject = new ReplaySubject<string>(1);
    public tasks$: Observable<Array<ITask>>;
    public serviceItems$: Observable<Array<IServiceItem>>;
    public userInfo$: Observable<IUserInfo>;
    private active = true;
    private _employeeId: string;
    @Input() public set EmployeeId(value: string) {
        this.log.verbose({
            type: 'TimeCardDocumentationFeatureComponent:EmployeeId:set',
            value
        });
        this._employeeId = value;
        this.EmployeeIdSubject.next(value);
    }
    public get EmployeeId(): string {
        return this._employeeId;
    }
    @Input() validating: boolean;
    @Input() approving: boolean;
    @Output() Save = new EventEmitter<Partial<TimeCardDocumentation>>();
    ShowErrorSubject = new ReplaySubject<fromShared.Errors.IShowErrors>(1);
    timecardClassDataService: EntityCollectionService<fromDataServices.TimecardClass.TimecardClass>;
    constructor(
        public entityServices: EntityServices,
        private confirmationService: ConfirmationService,
        private lookupStore: Store<fromLookups.LookupState>,
        private store: Store<docReducers.State>,
        private dispatcher: ActionsSubject,
        private log: LoggingService
    ) {
        this.environment = env.environment;
        this.timecardClassDataService =
            entityServices.getEntityCollectionService<fromDataServices.TimecardClass.TimecardClass>(
                fromDataServices.TimecardClass.entityName
            );
        this.store.dispatch(new fromLookups.LoadServiceItems(false));
    }

    ngOnDestroy() {
        this.active = false;
    }
    ngAfterViewInit(): void {
        this.setupObservables();
        this.setupSubscriptions();
    }
    setupObservables() {
        this.process$ = this.store.pipe(select(fromShared.selectCurrent));

        this.authClasses$ = this.lookupStore.pipe(
            select(fromLookups.getAuthClassesLookup)
        );
        this.selectedError$ = this.store.pipe(
            select(fromShared.Errors.getShowErrors)
        );
        this.includeClassify$ = this.lookupStore.pipe(
            select(fromUser.getUserInfo),
            map(x => x.TCIncludeClassify ?? false)
        );
        this.documentation$ = combineLatest([
            this.TimeCardsSubject,
            this.store.pipe(
                select(docReducers.selectAll),
                map(docs => clone(docs))
            )
        ]).pipe(
            tap(([timecards, docs]) =>
                this.log.verbose({
                    type: 'TimeCardDocumentationFeatureComponent:documentation$:tap',
                    timecards,
                    docs
                })
            ),
            map(
                ([tcs, docs]) =>
                    !!docs &&
                    !!tcs &&
                    (docs.map(doc => ({
                        ...doc,
                        timeCard: tcs.find(t => t.EmpTimeID === doc.EmpTimeID)
                    })) as TimeCardDocumentation[])
            ),
            map(x =>
                x.sort(
                    (a, b) =>
                        a.EmployeeWorkDate.getTime() -
                        b.EmployeeWorkDate.getTime()
                )
            ),
            tap(docs =>
                this.log.verbose({
                    type: 'TimeCardDocumentationFeatureComponent:documentation$',
                    docs
                })
            )
        );
        // this re-fetches the timecard docs if there is no doc in our list with the EmpTimeID of the timecard we're adding to
        this.AddDocumentation$.pipe(
            tap(added =>
                this.log.verbose({
                    type: 'TimeCardDocumentationFeatureComponent:AddDocumentation$:tap',
                    added
                })
            ),
            withLatestFrom(this.documentation$, this.process$),
            tap(([added, docs, process]) =>
                this.log.verbose({
                    type: 'TimeCardDocumentationFeatureComponent:AddDocumentation$:tap',
                    added,
                    docs,
                    process
                })
            ),
            filter(
                ([added, docs, process]) =>
                    !added ||
                    (added &&
                        !(
                            docs.findIndex(
                                doc => doc.EmpTimeID === added.EmpTimeID
                            ) > 0
                        ))
            ),
            tap(([added, docs, process]) =>
                this.log.verbose({
                    type: 'TimeCardDocumentationFeatureComponent:AddDocumentation$:filtered',
                    added,
                    docs,
                    process
                })
            ),
            takeWhile(() => this.active)
        ).subscribe(([added, docs, process]) => {
            this.store.dispatch(
                fromTimecard.getTimecardDocumentations({
                    id: this.EmployeeId,
                    processId: process.ProcessID
                })
            );
        });

        this.tasks$ = this.lookupStore.pipe(select(fromLookups.getTasksLookup));
        this.serviceItems$ = this.lookupStore.pipe(
            select(fromLookups.getServiceItemsLookup)
        );
        this.userInfo$ = this.lookupStore.pipe(select(fromUser.getUserInfo));
    }
    setupSubscriptions() {
        // TODO: do we need this?  timecardpresenter is taking userinfo merged with direct report select
        this.userInfo$.pipe(take(1)).subscribe(userInfo => {
            this.EmployeeIdSubject.next(userInfo.EmployeeListID);
        });
        this.EmployeeIdSubject.pipe(
            tap(x =>
                this.log.verbose({
                    type: 'TimeCardDocumentationFeatureComponent:EmployeeIdSubject',
                    x
                })
            ),
            filter(x => !!x),
            takeWhile(() => this.active),
            withLatestFrom(this.process$)
        ).subscribe(([employeeId, process]) => {
            this.lookupStore.dispatch(
                fromTimecard.getTimecardDocumentations({
                    id: employeeId,
                    processId: process.ProcessID
                })
            );
            this.timecardClassDataService.getWithQuery({
                EmployeeListID: employeeId
            });
        });
        combineLatest([
            this.documentation$,
            this.dispatcher.pipe(
                ofType(fromTimecard.updateTimecardDocumentationSuccess)
            )
        ])
            .pipe(takeWhile(() => this.active))
            .subscribe(([docs, update]) => {
                this.log.verbose({
                    type: 'TimeCardDocumentationFeatureComponent:UpdateSuccess:Save',
                    docs,
                    update
                });
                if (update.doc.EmpTimeID) {
                    const totalHours = this.getTotalHours(
                        docs,
                        update.doc.EmpTimeID
                    );
                    this.Save.next({ ...update.doc, Totalhours: totalHours });
                }
            });
        combineLatest([
            this.deleteSubject,
            this.documentation$,
            this.dispatcher.pipe(
                ofType(fromTimecard.updateTimecardDocumentationSuccess)
            )
        ])
            .pipe(takeWhile(() => this.active))
            .subscribe(([deleted, docs, update]) => {
                const timeId = deleted.EmpTimeID;
                const totalHours = this.getTotalHours(docs, deleted.EmpTimeID);
                this.log.verbose({
                    type: 'TimeCardDocumentationFeatureComponent:UpdateSuccess:deleteSubject',
                    deleted,
                    update,
                    totalHours
                });
                this.Save.next({ ...deleted, Totalhours: totalHours });
            });
        combineLatest([this.process$, this.ShowErrorSubject])
            .pipe(takeWhile(() => this.active))
            .subscribe(([process, event]) => {
                this.store.dispatch(
                    new fromShared.Errors.SetShowErrors({
                        open: event.open,
                        MsgEntityID: event.MsgEntityID,
                        MsgProcessID: process.ProcessID
                    })
                );
            });
    }
    getTotalHours(docs: TimeCardDocumentation[], timeId: string) {
        const timeDocs = docs.filter(x => x.EmpTimeID === timeId);
        const totalHours = timeDocs
            .map(x => x.Hours)
            .reduce((a, c) => a + c, 0);
        return totalHours;
    }
    onSave(documentation: Array<TimeCardDocumentation>) {
        this.log.verbose({
            type: 'TimeCardDocumentationFeatureComponent:onSave',
            documentation
        });
        this.store.dispatch(
            fromTimecard.putTimecardDocumentation({ doc: documentation })
        );
    }
    onDelete(doc: TimeCardDocumentation) {
        this.confirmationService.confirm({
            message: `Confirm removal of timecard documentation for: ${
                doc.TaskLongName
            } on ${moment(doc.EmployeeWorkDate).format('MM/DD/YYYY')}`,
            header: 'Confirmation',
            icon: 'pi pi-exclamation-triangle',
            acceptButtonStyleClass: 'p-button-danger p-button-text',
            rejectButtonStyleClass: 'p-button-text',
            accept: () => {
                this.store.dispatch(
                    fromTimecard.removeTimecardDocumentation({
                        ids: [doc.WorkID]
                    })
                );
                this.deleteSubject.next(doc);
            },
            reject: () => {
                // User cancelled, do nothing
            }
        });
    }
    onShowError(event) {
        this.ShowErrorSubject.next(event);
    }
    onDeleteAttachment(fileId: string) {
        this.confirmationService.confirm({
            message: 'Are you sure you want to delete this attachment?',
            header: 'Confirmation',
            icon: 'pi pi-exclamation-triangle',
            acceptButtonStyleClass: 'p-button-primary p-button-text',
            rejectButtonStyleClass: 'p-button-warning p-button-text',
            accept: () => {
                this.store.dispatch(
                    fromTimecard.removeTimecardDocumentationAttachment({
                        fileId
                    })
                );
            },
            reject: () => {
                // User cancelled, do nothing
            }
        });
    }

    onClose() {
        // this.dialogRef.close();
    }
    onUploadAttachment($event) {
        this.lookupStore.dispatch(
            fromTimecard.addTimecardDocumentationAttachment($event)
        );
    }
}
